// src/components/SearchPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { useSmartBack } from '../utils/useSmartBack.js';
import { 
  Search, User, Users, Building2, FileText, MapPin, Mail, Calendar, 
  Hash, Heart, MessageCircle, Share2, Eye, Filter, ChevronLeft, 
  ChevronRight, UserCheck, Award, Globe, Users2, Clock, Star, 
  TrendingUp, Bookmark, Share, ExternalLink, Grid, List, X,
  Mail as MailIcon, Phone, Tag, Target, CheckCircle, Users as UsersIcon,
  Image as ImageIcon, AlertCircle, Loader, UserPlus
} from 'lucide-react';
import './SearchPage.css';

const debounce = (fn, wait) => {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
};

function normalizeFollowingPayload(data) {
  const raw = data?.following ?? data?.follows ?? data?.data;
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (typeof entry === 'string') return { _id: entry };
        const id =
          entry?._id ??
          entry?.id ??
          entry?.userId ??
          entry?.followedUserId ??
          entry?.followedId;
        return id ? { _id: id, ...entry } : null;
      })
      .filter(Boolean);
  }
  if (Array.isArray(data)) {
    return data
      .map((entry) => {
        if (typeof entry === 'string') return { _id: entry };
        const id = entry?._id ?? entry?.id;
        return id ? { _id: id } : null;
      })
      .filter(Boolean);
  }
  return [];
}

function normalizeSentFollowRequestsPayload(data) {
  const raw = data?.requests ?? data?.data ?? data;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => {
      const requestId = entry?._id ?? entry?.id ?? entry?.requestId;
      const toUserId =
        entry?.toUser?._id ??
        entry?.toUser?.id ??
        entry?.toUserId ??
        entry?.userId;

      if (!requestId || !toUserId) return null;
      return {
        requestId: String(requestId),
        toUserId: String(toUserId),
        status: entry?.status ?? 'pending',
      };
    })
    .filter(Boolean);
}

// ── PostModal ────────────────────────────────────────────────────────────────
const PostModal = ({ post, onClose, getAvatarUrl, getUserDisplayName, formatDate, formatNumber, defaultAvatar }) => {
  const { t } = useTranslation();
  if (!post) return null;
  const authorName    = getUserDisplayName(post.author);
  const likesCount    = post.likes?.length    ?? post.likesCount    ?? 0;
  const commentsCount = post.comments?.length ?? post.commentsCount ?? 0;

  return (
    <div className="post-modal-overlay" onClick={onClose}>
      <div className="post-modal-content" onClick={e => e.stopPropagation()}>
        <button className="post-modal-close" onClick={onClose}><X size={20}/></button>

        <div className="post-modal-header">
          <img
            src={getAvatarUrl(post.author?.profileImage || post.author?.avatar)}
            alt={authorName}
            className="post-modal-avatar"
            onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }}
          />
          <div>
            <div className="post-modal-author">{authorName}</div>
            <div className="post-modal-time">{formatDate(post.createdAt)}</div>
          </div>
        </div>

        <p className="post-modal-body">{post.content}</p>

        {/* ── Post image(s) ── */}
        {(post.image || post.images?.[0]) && (
          <div className="post-modal-image">
            <img
              src={getAvatarUrl(post.image || post.images[0])}
              alt="Post"
              onError={e => { e.target.parentElement.style.display = 'none'; }}
            />
          </div>
        )}

        {post.hashtags?.length > 0 && (
          <div className="post-modal-tags">
            {post.hashtags.map((tag, i) => (
              <span key={i} className="grid-tag blue">#{tag}</span>
            ))}
          </div>
        )}

        <div className="post-modal-stats">
          <span><Heart size={15}/> {formatNumber(likesCount)} {t('pages.search.likes')}</span>
          <span><MessageCircle size={15}/> {formatNumber(commentsCount)} {t('pages.search.comments')}</span>
          {post.shares > 0 && <span><Share2 size={15}/> {formatNumber(post.shares)}</span>}
        </div>
      </div>
    </div>
  );
};

// ── SearchPage ───────────────────────────────────────────────────────────────
const SearchPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate  = useNavigate();
  const goBack = useSmartBack('/feed');

  const queryParams   = new URLSearchParams(location.search);
  const initialQuery  = queryParams.get('q')    || '';
  const initialType   = queryParams.get('type') || 'all';
  const initialPage   = parseInt(queryParams.get('page')) || 1;

  const [searchTerm,    setSearchTerm]    = useState(initialQuery);
  const [searchType,    setSearchType]    = useState(initialType);
  const [searchResults, setSearchResults] = useState({ users: [], posts: [], communities: [], organizations: [], programs: [] });
  const [loading,       setLoading]       = useState(false);
  const [following,     setFollowing]     = useState([]);
  const [pendingFollowRequests, setPendingFollowRequests] = useState({});
  const [isLoadingFollow, setIsLoadingFollow] = useState({});
  const [viewMode,      setViewMode]      = useState('grid');
  const [filters,       setFilters]       = useState({ sortBy: 'relevance', dateRange: 'all', category: 'all', location: '', verifiedOnly: false });
  const [filterOpen,    setFilterOpen]    = useState(false);
  const [currentPage,   setCurrentPage]   = useState(initialPage);
  const [totalPages,    setTotalPages]    = useState(1);
  const [totalCount,    setTotalCount]    = useState(0);
  const [hasSearched,   setHasSearched]   = useState(false);
  const [selectedPost,  setSelectedPost]  = useState(null);

  const trendingSearchesFallback = [
    'Education', 'Erasmus', 'Work', 'International', 'youthprograms',
    'summerjobs', 'scholarshipsearch', 'gapyear', 'Community', 'Organization',
    'Event', 'Study Abroad', 'Full Scholarships', 'Fellowship', 'Grants',
  ];
  const translatedTrending = t('pages.search.trendingTerms', { returnObjects: true });
  const trendingSearches = Array.isArray(translatedTrending) ? translatedTrending : trendingSearchesFallback;

  const defaultAvatar = "/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png";
  const itemsPerPage  = 12;

  const filtersRef     = useRef(filters);
  const searchTermRef  = useRef(searchTerm);
  const searchTypeRef  = useRef(searchType);
  const performSearchRef = useRef(null);
  const searchInputRef = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => { filtersRef.current    = filters;    }, [filters]);
  useEffect(() => { searchTermRef.current = searchTerm; }, [searchTerm]);
  useEffect(() => { searchTypeRef.current = searchType; }, [searchType]);

  // ── core search ──
  const performSearch = useCallback(async (term, type, page = 1, isNewSearch = false) => {
    if (!term.trim()) {
      setSearchResults({ users: [], posts: [], communities: [], organizations: [], programs: [] });
      setHasSearched(false);
      return;
    }
    
    setLoading(true);
    
    // If it's a new search, reset to page 1
    if (isNewSearch) {
      setCurrentPage(1);
      page = 1;
    }
    
    try {
      const token = localStorage.getItem('token');
      const f     = filtersRef.current;
      
      // Build URL with pagination parameters
      let url = `${GREENWICH_BACKEND_URL}/api/search?q=${encodeURIComponent(term)}&type=${type}&limit=${itemsPerPage}&page=${page}`;
      if (f.sortBy    !== 'relevance') url += `&sort=${f.sortBy}`;
      if (f.dateRange !== 'all')       url += `&date=${f.dateRange}`;
      if (f.location)                  url += `&location=${encodeURIComponent(f.location)}`;
      if (f.verifiedOnly)              url += `&verified=true`;

      console.log(`Searching page ${page}:`, url);

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();

        const num = (v, arr) => {
          if (v !== undefined && v !== null && v !== '') {
            const n = Number(v);
            if (!Number.isNaN(n)) return n;
          }
          return Array.isArray(arr) ? arr.length : 0;
        };
        const mapUser = (u, accountType) => ({
          ...u,
          accountType: accountType || u.accountType || 'user',
          followersCount: num(u.followersCount, u.followers),
          postsCount: num(u.postsCount, u.posts),
        });
        const organizersList = (data.organizers || []).map((u) => mapUser(u, 'organization'));
        // data.users already contains every matched user, including everyone already in
        // data.organizers/data.players — concatenating all three duplicated each person.
        const seenUserIds = new Set();
        const allUsers = [
          ...organizersList,
          ...(data.players || []).map((u) => mapUser(u, 'player')),
          ...(data.users || []).map((u) => mapUser(u, u.accountType || 'user')),
        ].filter((u) => {
          if (!u._id || seenUserIds.has(u._id)) return false;
          seenUserIds.add(u._id);
          return true;
        });
        const communities = data.communities || data.groups || [];
        const posts       = data.posts || [];
        const programs    = data.programs || [];

        setSearchResults({ users: allUsers, posts, communities, organizations: organizersList, programs });
        setHasSearched(true);

        // Get total count from response or calculate
        const tc = data.totalCount || data.total || data.pagination?.totalCount ||
                  allUsers.length + posts.length + communities.length;
        const tp = data.totalPages || data.pagination?.totalPages ||
                  Math.max(1, Math.ceil(tc / itemsPerPage));

        setTotalCount(tc);
        setTotalPages(tp);

        console.log(`Page ${page} of ${tp}, total items: ${tc}`);

        // Best-effort search logging — never blocks or surfaces errors to the user
        if (page === 1) {
          fetch(`${GREENWICH_BACKEND_URL}/api/search/log`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ query: term, resultsCount: tc })
          }).catch(() => {});
        }
      }
    } catch (e) { console.error('Search error:', e); }
    finally     { setLoading(false); }
  }, []);

  // Save performSearch to ref for use in other effects
  useEffect(() => {
    performSearchRef.current = performSearch;
  }, [performSearch]);

  const debouncedSearch = useCallback(
    debounce((term, type) => {
      if (performSearchRef.current) {
        performSearchRef.current(term, type, 1, true);
      }
    }, 400),
    [],
  );

  useEffect(() => () => debouncedSearch.cancel?.(), [debouncedSearch]);

  // ── load following ──
  const loadFollowing = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${GREENWICH_BACKEND_URL}/api/users/following`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFollowing(normalizeFollowingPayload(data));
      }
    } catch (e) { console.error('Error loading following:', e); }
  };

  const loadPendingFollowRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${GREENWICH_BACKEND_URL}/api/follow/requests/sent`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const normalized = normalizeSentFollowRequestsPayload(data);
      const next = normalized.reduce((acc, entry) => {
        if (entry.status === 'pending') {
          acc[entry.toUserId] = entry.requestId;
        }
        return acc;
      }, {});
      setPendingFollowRequests(next);
    } catch (e) { console.error('Error loading pending follow requests:', e); }
  };

  const bumpFollowersInResults = (targetId, delta) => {
    const id = String(targetId);
    const patch = (arr) => {
      if (!Array.isArray(arr)) return arr;
      return arr.map((u) => {
        const uid = String(u._id ?? u.id ?? '');
        if (uid !== id) return u;
        const prev = Number(u.followersCount ?? u.followers?.length ?? 0) || 0;
        return { ...u, followersCount: Math.max(0, prev + delta) };
      });
    };
    setSearchResults((prev) => ({
      ...prev,
      users: patch(prev.users),
      organizations: patch(prev.organizations),
    }));
  };

  // ── initial load ──
  useEffect(() => {
    loadFollowing();
    loadPendingFollowRequests();
    
    // Only perform search if there's an initial query
    if (initialQuery.trim()) {
      performSearch(initialQuery, initialType, initialPage, false);
      setHasSearched(true);
    }
  }, []); // Run once on mount

  // ── sync URL with search params ──
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const params = new URLSearchParams();
      if (searchTerm.trim())   params.set('q',    searchTerm);
      if (searchType !== 'all') params.set('type', searchType);
      if (currentPage > 1)     params.set('page', currentPage);

      const newSearch = params.toString();
      if (location.search !== (newSearch ? `?${newSearch}` : '')) {
        navigate({ search: newSearch }, { replace: true });
      }
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchType, currentPage, navigate, location.search]);

  // ── handle page changes ──
  useEffect(() => {
    // Skip on first render if we already loaded with initialPage
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    if (searchTermRef.current.trim() && hasSearched && performSearchRef.current) {
      console.log('Page changed to:', currentPage);
      performSearchRef.current(searchTermRef.current, searchTypeRef.current, currentPage, false);
    }
  }, [currentPage, hasSearched]);

  // ── helpers ──
  const getAvatarUrl = (imagePath) => {
    if (!imagePath || ['default','null','undefined','','false'].includes(String(imagePath))) return defaultAvatar;
    if (imagePath.startsWith('http') || imagePath.startsWith('blob:') || imagePath.startsWith('data:')) return imagePath;
    if (imagePath.includes('uploads/')) {
      const idx = imagePath.indexOf('uploads/');
      return `${GREENWICH_BACKEND_URL}/${imagePath.substring(idx)}`;
    }
    if (imagePath.startsWith('/') && !imagePath.startsWith('/nkarner'))
      return `${GREENWICH_BACKEND_URL}${imagePath}`;
    if (imagePath.includes('nkarner/')) return imagePath;
    return defaultAvatar;
  };

  const getUserDisplayName = (user) => {
    if (!user) return t('pages.search.unknown');
    if (user.organizationName && user.accountType === 'organization') return user.organizationName;
    const full = `${user.name || ''} ${user.surname || ''}`.trim();
    return full || user.username || t('pages.search.unknownUser');
  };

  const getCurrentResults = () => {
    switch (searchType) {
      case 'users':         return searchResults.users.map(u => ({ ...u, _resultType: 'user' }));
      case 'organizations': return searchResults.organizations.map(u => ({ ...u, _resultType: 'organization' }));
      case 'communities':   return searchResults.communities.map(c => ({ ...c, _resultType: 'community' }));
      case 'posts':         return searchResults.posts.map(p => ({ ...p, _resultType: 'post' }));
      case 'hashtags':      return searchResults.posts.map(p => ({ ...p, _resultType: 'post' })); // hashtag search is a filtered post search
      case 'projects':      return searchResults.programs.map(p => ({ ...p, _resultType: 'program' }));
      default: return [
        // searchResults.organizations is just the subset of searchResults.users whose
        // accountType is 'organization' — concatenating both here used to show every
        // organization twice (once as a "User" card, once as an "Organization" card).
        ...searchResults.users.map(u => ({
          ...u,
          _resultType: (u.accountType === 'organization' || u.accountType === 'organizer') ? 'organization' : 'user',
        })),
        ...searchResults.communities.map(c => ({ ...c, _resultType: 'community' })),
        ...searchResults.posts.map(p => ({ ...p, _resultType: 'post' })),
        ...searchResults.programs.map(p => ({ ...p, _resultType: 'program' })),
      ];
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    const input = e.target;
    setSearchTerm(value);
    setTimeout(() => input?.focus?.(), 0);
    // Don't reset page here - debounced search will handle it with isNewSearch=true
    if (value.trim()) {
      debouncedSearch(value, searchType);
    } else {
      setSearchResults({ users: [], posts: [], communities: [], organizations: [], programs: [] });
      setHasSearched(false);
      setCurrentPage(1);
    }
  };

  const handleTypeChange = (type) => {
    setSearchType(type);
    setCurrentPage(1); // Reset to page 1 when changing type
    if (searchTerm.trim() && performSearchRef.current) {
      performSearchRef.current(searchTerm, type, 1, true);
    }
  };

  // ── Follow / Unfollow ────────────────────────────────────────────────────
  const handleFollow = async (userId, accountType, e) => {
    if (e) e.stopPropagation();
    if (!userId) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const isOrganizer          = accountType === 'organization' || accountType === 'organizer';
    const sid = String(userId);
    const isCurrentlyFollowing = following.some(u => String(u._id || u.id || u) === sid);
    const pendingRequestId = pendingFollowRequests[sid];

    setIsLoadingFollow(prev => ({ ...prev, [userId]: true }));

    try {
      if (isOrganizer) {
        if (pendingRequestId) {
          let res = await fetch(`${GREENWICH_BACKEND_URL}/api/follow/requests/${pendingRequestId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (!res.ok) {
            res = await fetch(`${GREENWICH_BACKEND_URL}/api/follow/request/${pendingRequestId}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
          }
          if (res.ok) {
            setPendingFollowRequests(prev => {
              const next = { ...prev };
              delete next[sid];
              return next;
            });
          }
        } else if (!isCurrentlyFollowing) {
          // Organizations can be private too — the backend now replies with a pending
          // follow request instead of an instant follow when that's the case, so this
          // has to check the payload rather than treating any 200 as "now following".
          const res = await fetch(`${GREENWICH_BACKEND_URL}/api/users/${userId}/follow`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (res.ok) {
            const payload = await res.json().catch(() => null);
            if (payload?.followedDirectly !== false) {
              setFollowing(prev => [...prev, { _id: userId }]);
              bumpFollowersInResults(userId, 1);
            } else if (payload?.requestId) {
              setPendingFollowRequests(prev => ({ ...prev, [sid]: String(payload.requestId) }));
            }
          }
        } else {
          const res = await fetch(`${GREENWICH_BACKEND_URL}/api/unfollow/${userId}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (res.ok) {
            setFollowing(prev => prev.filter(u => String(u._id || u.id || u) !== sid));
            bumpFollowersInResults(userId, -1);
          }
        }
      } else {
        if (pendingRequestId) {
          let res = await fetch(`${GREENWICH_BACKEND_URL}/api/follow/requests/${pendingRequestId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (!res.ok) {
            res = await fetch(`${GREENWICH_BACKEND_URL}/api/follow/request/${pendingRequestId}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
          }
          if (res.ok) {
            setPendingFollowRequests(prev => {
              const next = { ...prev };
              delete next[sid];
              return next;
            });
          }
        } else if (!isCurrentlyFollowing) {
          let res = await fetch(`${GREENWICH_BACKEND_URL}/api/users/${userId}/follow-request`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          let payload = null;
          let usedDirectFollow = false;

          if (res.ok) {
            payload = await res.json().catch(() => null);
          }

          if (!res.ok) {
            res = await fetch(`${GREENWICH_BACKEND_URL}/api/users/${userId}/follow`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            usedDirectFollow = res.ok;
            if (res.ok) {
              payload = await res.json().catch(() => null);
            }
          }

          if (res.ok) {
            const followedDirectly = usedDirectFollow || payload?.followedDirectly === true;
            if (followedDirectly) {
              setFollowing(prev => [...prev, { _id: userId }]);
              bumpFollowersInResults(userId, 1);
              setPendingFollowRequests(prev => {
                const next = { ...prev };
                delete next[sid];
                return next;
              });
            } else if (payload?.requestId) {
              setPendingFollowRequests(prev => ({ ...prev, [sid]: String(payload.requestId) }));
            }
          }
        } else {
          const res = await fetch(`${GREENWICH_BACKEND_URL}/api/unfollow/${userId}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (res.ok) {
            setFollowing(prev => prev.filter(u => String(u._id || u.id || u) !== sid));
            bumpFollowersInResults(userId, -1);
          }
        }
      }
      await Promise.all([loadFollowing(), loadPendingFollowRequests()]);
    } catch (err) { console.error('Follow error:', err); }
    finally { setIsLoadingFollow(prev => ({ ...prev, [userId]: false })); }
  };

  const isFollowingUser = (userId) => {
    if (userId == null) return false;
    const sid = String(userId);
    return following.some(u => String(u._id || u.id || u) === sid);
  };

  const hasPendingFollowRequest = (userId) => {
    if (userId == null) return false;
    return !!pendingFollowRequests[String(userId)];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString), now = new Date();
    const days = Math.floor((now - d) / 86400000);
    if (days === 0) return t('pages.search.today');
    if (days === 1) return t('pages.search.yesterday');
    if (days < 7)  return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
    if (num >= 1_000)     return (num / 1_000).toFixed(1) + 'K';
    return num.toString();
  };

  const clearSearch = () => {
    setSearchTerm('');
    setHasSearched(false);
    setSearchResults({ users: [], posts: [], communities: [], organizations: [], programs: [] });
    setCurrentPage(1);
  };

  const handleTrendingClick = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    if (performSearchRef.current) {
      performSearchRef.current(term, searchType, 1, true);
    }
  };

  const getTypeColor = (type) => ({
    users: '#3b82f6', organizations: '#8b5cf6', communities: '#f59e0b', posts: '#ef4444',
    projects: '#22c55e', hashtags: '#06b6d4',
  }[type] || '#32614d');

  const tabs = [
    { id: 'all',           label: t('pages.search.tabAll'),           icon: Search   },
    { id: 'users',         label: t('pages.search.tabUsers'),         icon: User     },
    { id: 'organizations', label: t('pages.search.tabOrganizations'), icon: Building2 },
    { id: 'projects',      label: t('pages.search.tabProjects'),      icon: Target   },
    { id: 'communities',   label: t('pages.search.tabCommunities'),   icon: Users2   },
    { id: 'posts',         label: t('pages.search.tabPosts'),         icon: FileText },
    { id: 'hashtags',      label: t('pages.search.tabHashtags'),      icon: Hash     },
  ];

  const results      = getCurrentResults();
  const totalResults = results.length;

  // ── Pagination ──
  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    console.log('Going to page:', page);
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, '…', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1, '…', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '…', currentPage - 1, currentPage, currentPage + 1, '…', totalPages);
    }
    return pages;
  };

  // ── tab counts ──
  const getTabCount = (tabId) => {
    if (!searchTerm.trim() || !hasSearched) return 0;
    switch (tabId) {
      case 'all':           return totalCount || totalResults;
      case 'users':         return searchResults.users.length;
      case 'organizations': return searchResults.organizations.length;
      case 'communities':   return searchResults.communities.length;
      case 'posts':         return searchResults.posts.length;
      case 'hashtags':      return searchResults.posts.length;
      case 'projects':      return searchResults.programs.length;
      default:              return 0;
    }
  };

  return (
    <div className="search-page-container">

      {/* PostModal */}
      {selectedPost && (
        <PostModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          getAvatarUrl={getAvatarUrl}
          getUserDisplayName={getUserDisplayName}
          formatDate={formatDate}
          formatNumber={formatNumber}
          defaultAvatar={defaultAvatar}
        />
      )}

      {/* ── Hero ── */}
      <div className="search-hero">
        <div className="search-hero-content">
          <div className="hero-header">
            <button className="back-btn" onClick={goBack}><ChevronLeft size={24}/></button>
            <h1>{t('pages.search.heroTitle')}</h1>
          </div>
          <p className="hero-subtitle">{t('pages.search.heroSubtitle')}</p>

          <div className="search-bar-container">
            <div className="search-input-wrapper">
              <Search className="search-icon" size={24}/>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={t('pages.search.searchPlaceholder')}
                className="search-input"
              />
              {searchTerm && (
                <button onClick={clearSearch} className="clear-search-btn"><X size={20}/></button>
              )}
            </div>
            <button className={`filter-btn ${filterOpen ? 'active' : ''}`} onClick={() => setFilterOpen(!filterOpen)}>
              <Filter size={20}/> {t('pages.search.filtersBtn')}
            </button>
          </div>

          {filterOpen && (
            <div className="search-filters-container">
              <div className="search-filters-wrapper">
                <div className="filter-group">
                  <label>{t('pages.search.sortBy')}</label>
                  <select value={filters.sortBy} onChange={e => setFilters(p => ({ ...p, sortBy: e.target.value }))} className="filter-select">
                    <option value="relevance">{t('pages.search.sortRelevance')}</option>
                    <option value="newest">{t('pages.search.sortNewest')}</option>
                    <option value="popular">{t('pages.search.sortPopular')}</option>
                    <option value="name">{t('pages.search.sortName')}</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>{t('pages.search.dateRange')}</label>
                  <select value={filters.dateRange} onChange={e => setFilters(p => ({ ...p, dateRange: e.target.value }))} className="filter-select">
                    <option value="all">{t('pages.search.dateAll')}</option>
                    <option value="today">{t('pages.search.dateToday')}</option>
                    <option value="week">{t('pages.search.dateWeek')}</option>
                    <option value="month">{t('pages.search.dateMonth')}</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>{t('pages.search.locationLabel')}</label>
                  <input
                    type="text"
                    value={filters.location}
                    onChange={e => setFilters(p => ({ ...p, location: e.target.value }))}
                    placeholder={t('pages.search.locationPlaceholder')}
                    className="filter-input"
                  />
                </div>
                <div className="filter-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={filters.verifiedOnly}
                      onChange={e => setFilters(p => ({ ...p, verifiedOnly: e.target.checked }))}
                      className="checkbox"
                    />
                    <span>{t('pages.search.verifiedOnly')}</span>
                  </label>
                </div>
                <button
                  onClick={() => setFilters({ sortBy: 'relevance', dateRange: 'all', category: 'all', location: '', verifiedOnly: false })}
                  className="clear-filters-btn"
                >
                  {t('pages.search.clearFilters')}
                </button>
              </div>
            </div>
          )}

          <div className="search-tabs-wrapper">
            <div className="search-tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTypeChange(tab.id)}
                  className={`search-tab ${searchType === tab.id ? 'active' : ''}`}
                  style={{ '--tab-color': getTypeColor(tab.id) }}
                >
                  <tab.icon size={18}/>
                  <span>{tab.label}</span>
                  {searchTerm.trim() && hasSearched && (
                    <span className="tab-count">{formatNumber(getTabCount(tab.id))}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="search-main-content">

        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"/>
            <p>{t('pages.search.searchingFor', { term: searchTerm })}</p>
          </div>
        )}

        {!loading && !searchTerm.trim() && (
          <div className="empty-state">
            <div className="empty-state-icon"><Search size={64}/></div>
            <h2>{t('pages.search.emptyTitle')}</h2>
            <p className="empty-state-description">{t('pages.search.emptyDescription')}</p>
            <div className="search-categories">
              {tabs.slice(1).map(tab => (
                <button key={tab.id} onClick={() => handleTypeChange(tab.id)} className="search-category-card" style={{ borderColor: getTypeColor(tab.id) }}>
                  <div className="category-icon" style={{ backgroundColor: `${getTypeColor(tab.id)}20`, color: getTypeColor(tab.id) }}>
                    <tab.icon size={28}/>
                  </div>
                  <h3>{tab.label}</h3>
                  <p>{t('pages.search.browseCategory', { category: tab.label.toLowerCase() })}</p>
                </button>
              ))}
            </div>
            <div className="trending-searches">
              <h3><TrendingUp size={20}/> {t('pages.search.trendingHeading')}</h3>
              <div className="trending-tags">
                {trendingSearches.map(term => (
                  <button key={term} onClick={() => handleTrendingClick(term)} className="trending-tag">
                    <Target size={14}/>{term}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {!loading && searchTerm.trim() && hasSearched && (
          <div className="search-results-container">
            <div className="view-controls">
              <div className="results-info">
                <h3>
                  {t('pages.search.resultsHeading')} <span className="results-count">({formatNumber(totalCount || totalResults)})</span>
                </h3>
                {totalPages > 1 && (
                  <span className="page-info"> ({t('pages.search.pageOf', { current: currentPage, total: totalPages })})</span>
                )}
              </div>
              <div className="view-mode-controls">
                <button className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}><Grid size={20}/></button>
                <button className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}><List size={20}/></button>
              </div>
            </div>

            {totalResults === 0 ? (
              <div className="no-results-found">
                <div className="no-results-icon"><Search size={64}/></div>
                <h3>{t('pages.search.noResultsFor', { term: searchTerm })}</h3>
                <p>{t('pages.search.noResultsHint')}</p>
              </div>
            ) : (
              <>
                <div className={`results-container ${viewMode}`}>
                  {results.map((item, idx) => (
                    <SearchResultCard
                      key={`${item._resultType || 'item'}-${item._id || item.id || idx}`}
                      item={item}
                      viewMode={viewMode}
                      onFollow={handleFollow}
                      isFollowing={isFollowingUser(item._id || item.id)}
                      hasPendingRequest={hasPendingFollowRequest(item._id || item.id)}
                      isLoadingFollow={isLoadingFollow[item._id || item.id]}
                      navigate={navigate}
                      getAvatarUrl={getAvatarUrl}
                      getUserDisplayName={getUserDisplayName}
                      formatDate={formatDate}
                      formatNumber={formatNumber}
                      defaultAvatar={defaultAvatar}
                      onPostClick={post => setSelectedPost(post)}
                    />
                  ))}
                </div>

                {/* ── Pagination ── */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn prev"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={20}/> {t('pages.search.previous')}
                    </button>

                    <div className="page-numbers">
                      {pageNumbers().map((p, i) =>
                        p === '…'
                          ? <span key={`ellipsis-${i}`} className="page-ellipsis">…</span>
                          : <button
                              key={p}
                              className={`page-btn ${currentPage === p ? 'active' : ''}`}
                              onClick={() => goToPage(p)}
                            >{p}</button>
                      )}
                    </div>

                    <button
                      className="pagination-btn next"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      {t('pages.search.next')} <ChevronRight size={20}/>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── SearchResultCard ──────────────────────────────────────────────────────────
const SearchResultCard = ({
  item, viewMode, onFollow, isFollowing, hasPendingRequest, isLoadingFollow,
  navigate, getAvatarUrl, getUserDisplayName, formatDate, formatNumber,
  defaultAvatar, onPostClick,
}) => {
  const { t } = useTranslation();
  const type   = item._resultType || item.type || item.accountType || 'user';
  const isUser = type === 'user' || type === 'player';
  const isOrg  = type === 'organization' || type === 'organizer';
  const isCom  = type === 'community';
  const isPost = type === 'post';
  const isProgram = type === 'program';

  const itemId = item._id || item.id;

  const getAvatar = () => {
    if (isPost) return getAvatarUrl(item.author?.profileImage || item.author?.avatar);
    if (isProgram) return getAvatarUrl(item.image_url);
    return getAvatarUrl(item.profileImage || item.avatar || item.image || item.logo);
  };

  // ── click handlers ──
  const handleClick = () => {
    if (isUser)      navigate(`/profile/${itemId}`);
    else if (isOrg)  navigate(`/organization/${itemId}`);
    else if (isCom)  navigate(`/community/group/${itemId}`);
    else if (isPost) onPostClick(item);
    else if (isProgram) navigate(`/programs?program=${itemId}`);
  };

  const followersCount = item.followersCount ?? item.followers?.length ?? 0;
  const postsCount     = item.postsCount     ?? item.posts?.length     ?? 0;
  const membersCount   = item.members?.length ?? item.memberCount      ?? 0;
  const likesCount     = item.likes?.length   ?? item.likesCount       ?? 0;
  const commentsCount  = item.comments?.length ?? item.commentsCount   ?? 0;

  const displayName = isPost ? getUserDisplayName(item.author) : isProgram ? (item.title || t('pages.search.untitledProgram')) : getUserDisplayName(item);

  // ── post thumbnail src (handles array or single) ──
  const postImageSrc = item.image || item.images?.[0] || null;

  // ── List view ──
  if (viewMode === 'list') {
    return (
      <div
        className={`result-card list ${isUser ? 'user' : isOrg ? 'org' : isCom ? 'community' : isProgram ? 'program' : 'post'}`}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <div className="card-left">
          <div className="list-avatar-wrap">
            <img
              src={getAvatar()} alt={displayName} className={`list-avatar ${isCom || isProgram ? 'square' : ''}`}
              onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }}
            />
            {item.isVerified && <div className="verified-dot"><UserCheck size={10}/></div>}
          </div>
        </div>

        <div className="card-middle">
          <div className="list-name-row">
            <span className="list-name">{displayName}</span>
            {(isUser || isOrg) && item.isVerified && <span className="verified-chip"><UserCheck size={11}/></span>}
            <span className={`type-pill ${isUser ? 'blue' : isOrg ? 'purple' : isCom ? 'amber' : isProgram ? 'green' : 'red'}`}>
              {isUser ? t('pages.search.pillUser') : isOrg ? t('pages.search.pillOrg') : isCom ? t('pages.search.pillCommunity') : isProgram ? t('pages.search.pillProgram') : t('pages.search.pillPost')}
            </span>
          </div>

          {(isUser || isOrg) && item.username   && <div className="list-handle">@{item.username}</div>}
          {(isUser || isOrg) && item.bio        && <p className="list-bio">{item.bio.substring(0, 140)}{item.bio.length > 140 ? '…' : ''}</p>}
          {isCom  && item.description           && <p className="list-bio">{item.description.substring(0, 140)}{item.description.length > 140 ? '…' : ''}</p>}
          {isPost && item.content               && <p className="list-bio">{item.content.substring(0, 180)}{item.content.length > 180 ? '…' : ''}</p>}
          {isProgram && item.description         && <p className="list-bio">{item.description.substring(0, 160)}{item.description.length > 160 ? '…' : ''}</p>}

          {/* post thumbnail in list view */}
          {isPost && postImageSrc && (
            <div className="list-post-thumb">
              <img
                src={getAvatarUrl(postImageSrc)} alt="Post thumbnail"
                onError={e => { e.target.parentElement.style.display = 'none'; }}
              />
            </div>
          )}

          <div className="list-meta">
            {(isUser || isOrg) && (
              <>
                <span className="meta-chip"><UsersIcon size={12}/> {formatNumber(followersCount)} {t('pages.search.metaFollowers')}</span>
                <span className="meta-chip"><FileText   size={12}/> {formatNumber(postsCount)} {t('pages.search.metaPosts')}</span>
                {item.location && <span className="meta-chip"><MapPin size={12}/> {item.location}</span>}
              </>
            )}
            {isCom && (
              <>
                <span className="meta-chip"><Users    size={12}/> {formatNumber(membersCount)} {t('pages.search.metaMembers')}</span>
                <span className="meta-chip"><FileText size={12}/> {formatNumber(postsCount)} {t('pages.search.metaPosts')}</span>
                {item.location && <span className="meta-chip"><MapPin size={12}/> {item.location}</span>}
              </>
            )}
            {isPost && (
              <>
                <span className="meta-chip"><Heart         size={12}/> {formatNumber(likesCount)}</span>
                <span className="meta-chip"><MessageCircle size={12}/> {formatNumber(commentsCount)}</span>
                <span className="meta-chip"><Clock         size={12}/> {formatDate(item.createdAt)}</span>
              </>
            )}
            {isProgram && (
              <>
                {item.organizationName && <span className="meta-chip"><Building2 size={12}/> {item.organizationName}</span>}
                {item.location && <span className="meta-chip"><MapPin size={12}/> {item.location}</span>}
                {item.deadline && <span className="meta-chip"><Calendar size={12}/> {item.deadline}</span>}
              </>
            )}
          </div>
        </div>

        <div className="card-right" onClick={e => e.stopPropagation()}>
          {(isUser || isOrg) && (
            <button
              className={`follow-btn-sp ${(isFollowing || hasPendingRequest) ? 'following' : ''}`}
              disabled={isLoadingFollow}
              onClick={e => onFollow(itemId, item.accountType, e)}
            >
              {isLoadingFollow
                ? <div className="btn-spin"/>
                : isFollowing
                  ? <><UserCheck size={14}/> {t('pages.search.btnFollowing')}</>
                  : hasPendingRequest
                    ? <><Clock size={14}/> {t('pages.search.btnRequested')}</>
                  : <><UserPlus  size={14}/> {t('pages.search.btnFollow')}</>
              }
            </button>
          )}
          <button className="view-btn-sp" onClick={handleClick}>
            {isCom ? t('pages.search.viewCommunity') : isPost ? t('pages.search.viewPost') : isOrg ? t('pages.search.viewOrg') : isProgram ? t('pages.search.viewProgram') : t('pages.search.viewProfile')}
          </button>
        </div>
      </div>
    );
  }

  // ── Grid view ──
  return (
    <div
      className={`result-card grid ${isUser ? 'user' : isOrg ? 'org' : isCom ? 'community' : isProgram ? 'program' : 'post'}`}
      onClick={handleClick}
      style={{ cursor: 'pointer' }}
    >
      <div className={`card-accent ${isUser ? 'blue' : isOrg ? 'purple' : isCom ? 'amber' : isProgram ? 'green' : 'red'}`}/>

      {/* ── User / Org card ── */}
      {(isUser || isOrg) && (
        <>
          <div className="grid-avatar-wrap">
            <img
              src={getAvatar()} alt={displayName} className="grid-avatar"
              onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }}
            />
            {item.isVerified && <div className="grid-verified"><UserCheck size={11}/></div>}
          </div>
          <div className={`grid-type-badge ${isOrg ? 'purple' : 'blue'}`}>
            {isOrg ? <Building2 size={12}/> : <User size={12}/>}
            {isOrg ? t('pages.search.pillOrganizationFull') : t('pages.search.pillUser')}
          </div>
          <h3 className="grid-name">{displayName}</h3>
          {item.username && <p className="grid-handle">@{item.username}</p>}
          {item.bio      && <p className="grid-bio">{item.bio.substring(0, 75)}{item.bio.length > 75 ? '…' : ''}</p>}
          <div className="grid-stats">
            <div className="grid-stat">
              <span className="stat-val">{formatNumber(followersCount)}</span>
              <span className="stat-lbl">{t('pages.search.statFollowers')}</span>
            </div>
            <div className="stat-divider"/>
            <div className="grid-stat">
              <span className="stat-val">{formatNumber(postsCount)}</span>
              <span className="stat-lbl">{t('pages.search.statPosts')}</span>
            </div>
          </div>
          {item.location && <div className="grid-location"><MapPin size={12}/>{item.location}</div>}
          <div className="grid-actions" onClick={e => e.stopPropagation()}>
            <button
              className={`follow-btn-sp ${(isFollowing || hasPendingRequest) ? 'following' : ''}`}
              disabled={isLoadingFollow}
              onClick={e => onFollow(itemId, item.accountType, e)}
            >
              {isLoadingFollow
                ? <div className="btn-spin"/>
                : isFollowing
                  ? <><UserCheck size={14}/> {t('pages.search.btnFollowing')}</>
                  : hasPendingRequest
                    ? <><Clock size={14}/> {t('pages.search.btnRequested')}</>
                  : <><UserPlus  size={14}/> {t('pages.search.btnFollow')}</>
              }
            </button>
            <button className="view-btn-sp outline" onClick={handleClick}>{t('pages.search.viewShort')}</button>
          </div>
        </>
      )}

      {/* ── Community card ── */}
      {isCom && (
        <>
          <div className="grid-avatar-wrap">
            <img
              src={getAvatar()} alt={item.name} className="grid-avatar square"
              onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }}
            />
          </div>
          <div className="grid-type-badge amber"><Users2 size={12}/>{t('pages.search.pillCommunity')}</div>
          <h3 className="grid-name">{item.name}</h3>
          {item.description && (
            <p className="grid-bio">{item.description.substring(0, 80)}{item.description.length > 80 ? '…' : ''}</p>
          )}
          <div className="grid-stats">
            <div className="grid-stat">
              <span className="stat-val">{formatNumber(membersCount)}</span>
              <span className="stat-lbl">{t('pages.search.statMembers')}</span>
            </div>
            <div className="stat-divider"/>
            <div className="grid-stat">
              <span className="stat-val">{formatNumber(postsCount)}</span>
              <span className="stat-lbl">{t('pages.search.statPosts')}</span>
            </div>
          </div>
          {item.tags?.length > 0 && (
            <div className="grid-tags">
              {item.tags.slice(0, 3).map((tag, i) => <span key={i} className="grid-tag">#{tag}</span>)}
            </div>
          )}
          <div className="grid-actions" onClick={e => e.stopPropagation()}>
            <button className="view-btn-sp full amber" onClick={handleClick}>{t('pages.search.viewCommunity')}</button>
          </div>
        </>
      )}

      {/* ── Post card ── */}
      {isPost && (
        <>
          <div className="post-author-row">
            <img
              src={getAvatar()} alt={displayName} className="post-author-avatar"
              onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }}
            />
            <div>
              <div className="post-author-name">{displayName}</div>
              <div className="post-time">{formatDate(item.createdAt)}</div>
            </div>
            <div className="grid-type-badge red ml-auto"><FileText size={12}/>{t('pages.search.pillPost')}</div>
          </div>

          <p className="post-body">
            {item.content?.substring(0, 130)}{item.content?.length > 130 ? '…' : ''}
          </p>

          {/* ── Post image thumbnail ── */}
          {postImageSrc && (
            <div className="post-thumb">
              <img
                src={getAvatarUrl(postImageSrc)}
                alt="Post"
                onError={e => { e.target.parentElement.style.display = 'none'; }}
              />
            </div>
          )}

          {item.hashtags?.length > 0 && (
            <div className="grid-tags">
              {item.hashtags.slice(0, 3).map((tag, i) => <span key={i} className="grid-tag blue">#{tag}</span>)}
            </div>
          )}

          <div className="post-stats-row">
            <span><Heart size={13}/> {formatNumber(likesCount)}</span>
            <span><MessageCircle size={13}/> {formatNumber(commentsCount)}</span>
            {item.shares > 0 && <span><Share2 size={13}/> {formatNumber(item.shares)}</span>}
          </div>

          <div className="grid-actions" onClick={e => e.stopPropagation()}>
            <button className="view-btn-sp full red" onClick={handleClick}>{t('pages.search.viewPost')}</button>
          </div>
        </>
      )}

      {/* ── Program / opportunity card ── */}
      {isProgram && (
        <>
          <div className="grid-avatar-wrap">
            <img
              src={getAvatar()} alt={displayName} className="grid-avatar square"
              onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }}
            />
          </div>
          <div className="grid-type-badge green"><Target size={12}/>{t('pages.search.pillProgram')}</div>
          <h3 className="grid-name">{displayName}</h3>
          {item.organizationName && <p className="grid-handle">{item.organizationName}</p>}
          {item.description && (
            <p className="grid-bio">{item.description.substring(0, 90)}{item.description.length > 90 ? '…' : ''}</p>
          )}
          <div className="grid-stats">
            {item.location && <div className="grid-location"><MapPin size={12}/>{item.location}</div>}
            {item.deadline && <div className="grid-location"><Calendar size={12}/>{item.deadline}</div>}
          </div>
          {item.categories?.length > 0 && (
            <div className="grid-tags">
              {item.categories.slice(0, 3).map((cat, i) => <span key={i} className="grid-tag">{cat}</span>)}
            </div>
          )}
          <div className="grid-actions" onClick={e => e.stopPropagation()}>
            <button className="view-btn-sp full" style={{ background: '#22c55e' }} onClick={handleClick}>{t('pages.search.viewProgram')}</button>
          </div>
        </>
      )}
    </div>
  );
};

export default SearchPage;
